import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

export interface WizardStep {
  id: string;
  label?: string; // Optional label, will use translation if not provided
  completed: boolean;
  current: boolean;
}

interface WizardStepperProps {
  steps: WizardStep[];
}

/**
 * Visual stepper component for multi-step wizard
 * Shows progress through wizard steps with visual indicators
 */
export function WizardStepper({ steps }: WizardStepperProps) {
  const { t } = useLanguage();
  
  // Map step IDs to translated labels
  const getStepLabel = (step: WizardStep): string => {
    if (step.label) {
      return step.label;
    }
    const stepKey = step.id as keyof typeof t.wizard.steps;
    return t.wizard.steps[stepKey] || step.id;
  };

  return (
    <nav aria-label="Progress" className='lg:mb-16'>
      {/* Mobile/Tablet: Dot stepper */}
      <ol className="flex lg:hidden items-center justify-center gap-2">
        {steps.map((step) => (
          <li key={step.id}>
            <div
              className={cn(
                'h-2 w-2 rounded-full transition-colors',
                (step.completed || step.current) && 'bg-primary',
                !step.completed && !step.current && 'bg-muted-foreground/30'
              )}
              aria-current={step.current ? 'step' : undefined}
            />
          </li>
        ))}
      </ol>

      {/* Desktop: Full stepper with labels */}
      <ol className="hidden lg:flex items-start justify-between w-full">
        {steps.map((step, index) => (
          <li
            key={step.id}
            className={cn(
              'relative flex items-start',
              index !== steps.length - 1 && 'flex-1'
            )}
          >
            {/* Circle and label column - fixed width to match circle */}
            <div className="relative flex flex-col items-center w-10">
              {/* Step circle */}
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold',
                  step.completed && !step.current && 'border-primary bg-primary text-primary-foreground',
                  step.current && 'border-primary bg-background text-primary',
                  !step.completed && !step.current && 'border-muted-foreground/30 bg-background text-muted-foreground'
                )}
              >
                {step.completed && !step.current ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>

              {/* Step label below circle - absolutely positioned to not affect layout */}
              <span
                className={cn(
                  'absolute top-12 left-1/2 -translate-x-1/2 text-xs font-medium text-center whitespace-nowrap',
                  step.current && 'text-foreground',
                  !step.current && step.completed && 'text-muted-foreground',
                  !step.current && !step.completed && 'text-muted-foreground/60'
                )}
              >
                {getStepLabel(step)}
              </span>
            </div>

            {/* Connector line */}
            {index !== steps.length - 1 && (
              <div
                className={cn(
                  'h-0.5 flex-1 bg-muted-foreground/30 mt-5',
                  step.completed && 'bg-primary'
                )}
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
